import type { Tenant, ThemeMode } from '../types';
import { hexToHslObject, formatHsl, getContrastForeground, getSoftTint } from '../utils/colorUtils';

const MODE_TOKENS: Record<'dark' | 'light', Record<string, string>> = {
  dark: {
    background: '240 10% 3.9%',
    foreground: '0 0% 98%',
    card: '240 10% 5%',
    'card-foreground': '0 0% 98%',
    popover: '240 10% 5%',
    'popover-foreground': '0 0% 98%',
    muted: '240 3.7% 15.9%',
    'muted-foreground': '240 5% 64.9%'
  },
  light: {
    background: '210 40% 96%', // Soft pearl backdrop
    foreground: '222 47% 12%',
    card: '0 0% 100%',
    'card-foreground': '222 47% 12%',
    popover: '0 0% 100%',
    'popover-foreground': '222 47% 12%',
    muted: '210 30% 94%',
    'muted-foreground': '215 25% 45%'
  }
};

const resolveMode = (mode: ThemeMode): 'dark' | 'light' => {
  if (mode === 'system') {
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return mode;
};

export const applyThemeModeBase = (mode: ThemeMode) => {
  const resolvedMode = resolveMode(mode);
  const root = document.documentElement;
  root.classList.toggle('dark', resolvedMode === 'dark');
  root.classList.toggle('light', resolvedMode === 'light');
  root.dataset.themeMode = resolvedMode;

  const tokens = MODE_TOKENS[resolvedMode];
  Object.entries(tokens).forEach(([key, value]) => {
    root.style.setProperty(`--${key}`, value);
  });
};

/**
 * Applies the tenant's brand colors to the root CSS variables.
 * This drives the Tailwind `bg-primary`, `text-primary-foreground`, etc.
 */
export const applyTenantTheme = (tenant: Tenant, mode: ThemeMode = 'dark') => {
  if (!tenant.primaryColor) return;

  const resolvedMode = resolveMode(mode);
  const root = document.documentElement;
  const primaryHsl = hexToHslObject(tenant.primaryColor);
  const secondaryHex = tenant.secondaryColor ?? tenant.primaryColor;
  const secondaryHsl = hexToHslObject(secondaryHex);

  root.style.setProperty('--primary', formatHsl(primaryHsl));
  const foregroundHsl = getContrastForeground(tenant.primaryColor);
  root.style.setProperty('--primary-foreground', foregroundHsl);
  root.style.setProperty('--ring', formatHsl(primaryHsl));

  root.style.setProperty('--accent', getSoftTint(primaryHsl, resolvedMode));
  root.style.setProperty('--accent-foreground', formatHsl(primaryHsl));

  root.style.setProperty('--secondary', formatHsl(secondaryHsl));
  root.style.setProperty('--secondary-foreground', getContrastForeground(secondaryHex));

  const adjustedLightness = resolvedMode === 'dark'
    ? Math.min(95, secondaryHsl.l + 30)
    : Math.max(35, secondaryHsl.l - 8);
  const borderHsl = { ...secondaryHsl, l: adjustedLightness };
  const borderValue = formatHsl(borderHsl);
  root.style.setProperty('--border', borderValue);
  root.style.setProperty('--input', borderValue);

  // Theme applied: tenant.name [tenant.primaryColor] in resolvedMode mode
};

/**
 * Resets to default system theme
 */
export const resetTheme = () => {
  const root = document.documentElement;
  root.style.removeProperty('--primary');
  root.style.removeProperty('--primary-foreground');
  root.style.removeProperty('--ring');
  root.style.removeProperty('--accent');
  root.style.removeProperty('--accent-foreground');
  root.style.removeProperty('--secondary');
  root.style.removeProperty('--secondary-foreground');
  root.style.removeProperty('--border');
  root.style.removeProperty('--input');
};
